import type { GridColDef, GridInitialState } from '@mui/x-data-grid';

/**
 * Updates the column visibility model in the grid's initial state by hiding new fields
 * that are not present in the persisted initial state. This ensures that any new columns
 * introduced in the grid are hidden by default unless explicitly specified.
 *
 * @param columnDefinitions - The array of grid column definitions (`GridColDef[]`) that represent
 * the current set of columns in the grid.
 * @param initialState - The initial state of the grid (`GridInitialState`) which includes
 * the default column visibility model.
 * @param persistedInitialState - The persisted initial state of the grid (`GridInitialState`)
 * which contains the previously saved column visibility model.
 * @returns The updated grid initial state with the column visibility model adjusted to
 * include new fields, defaulting their visibility to `false` if not defined in the persisted state.
 *
 * @remarks
 * This function is required to handle scenarios where new columns are added to the grid
 * after a persisted state has been saved. Without this adjustment, new columns would
 * automatically be visible, potentially disrupting the user's saved preferences.
 */
export const hideNewFields = (
  columnDefinitions: GridColDef[],
  initialState: GridInitialState,
  persistedInitialState: GridInitialState,
) => {
  if (!persistedInitialState.columns?.columnVisibilityModel)
    return initialState;

  if (!initialState.columns) {
    return initialState;
  }

  const updatedColumnVisibilityModel = {
    ...persistedInitialState.columns.columnVisibilityModel,
  };

  columnDefinitions.forEach((column) => {
    if (updatedColumnVisibilityModel[column.field] === undefined) {
      updatedColumnVisibilityModel[column.field] =
        initialState.columns?.columnVisibilityModel?.[column.field] ?? false;
    }
  });

  return {
    ...initialState,
    columns: {
      ...initialState.columns,
      columnVisibilityModel: updatedColumnVisibilityModel,
    },
  };
};
